/**
 * Offline Queue — Iter. 406.fr.54
 * ────────────────────────────────
 * Persistent write-behind queue for mutations that must survive dropouts
 * in gym basements where connectivity flickers.
 *
 *   • Adds an entry to localStorage when a mutation is fired without
 *     internet, or when the network call fails.
 *   • Retries the queue on `online` events, on `visibilitychange`, and
 *     every 20 s while the tab is open.
 *   • Broadcasts `yumix:offline-queue-change` so any component (e.g.
 *     Dashboard, Hydration) can display a small "syncing" indicator.
 *   • Idempotency: each entry carries a `client_id` so the server can
 *     de-duplicate replays (backend can ignore unknown key; harmless).
 */

const KEY = 'yumix_offline_queue_v1';
const MAX_RETRIES = 6;

const readQueue = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
};
const writeQueue = (queue) => {
  try { localStorage.setItem(KEY, JSON.stringify(queue)); } catch { /* */ }
  try { window.dispatchEvent(new CustomEvent('yumix:offline-queue-change', { detail: { size: queue.length } })); } catch { /* */ }
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

let apiRef = null;
export const bindOfflineQueueApi = (api) => { apiRef = api; };

const executeEntry = async (entry) => {
  if (!apiRef) throw new Error('api-not-bound');
  const cfg = { headers: { 'X-Offline-Client-Id': entry.client_id } };
  switch (entry.method) {
    case 'post':   return apiRef.post(entry.url, entry.body, cfg);
    case 'put':    return apiRef.put(entry.url, entry.body, cfg);
    case 'patch':  return apiRef.patch(entry.url, entry.body, cfg);
    case 'delete': return apiRef.delete(entry.url, cfg);
    default:       throw new Error(`unsupported method ${entry.method}`);
  }
};

export const enqueueMutation = ({ method, url, body }) => {
  const entry = {
    client_id: uid(),
    method,
    url,
    body,
    attempts: 0,
    queued_at: new Date().toISOString(),
  };
  const q = readQueue();
  q.push(entry);
  writeQueue(q);
  // Attempt immediate flush on next tick; useful when the failure was a
  // 500 rather than a genuine offline event.
  setTimeout(() => { flushQueue(); }, 500);
  return entry.client_id;
};

let flushing = false;
export const flushQueue = async () => {
  if (flushing) return;
  if (!navigator.onLine) return;
  flushing = true;
  try {
    let q = readQueue();
    if (q.length === 0) return;
    const survivors = [];
    for (const entry of q) {
      try {
        await executeEntry(entry);
      } catch (err) {
        entry.attempts = (entry.attempts || 0) + 1;
        entry.last_error = err?.message || String(err);
        if (entry.attempts < MAX_RETRIES) {
          survivors.push(entry);
        } else {
          // Give up silently after MAX_RETRIES — keep in a "dead-letter"
          // slot so we can surface it in the UI if needed later.
          try {
            const dead = JSON.parse(localStorage.getItem('yumix_offline_dead_v1') || '[]');
            dead.push(entry);
            localStorage.setItem('yumix_offline_dead_v1', JSON.stringify(dead.slice(-25)));
          } catch { /* */ }
        }
      }
    }
    writeQueue(survivors);
  } finally {
    flushing = false;
  }
};

export const getQueueSize = () => readQueue().length;
export const clearQueue = () => writeQueue([]);

// Auto-flush hooks — mounted once at module import.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushQueue(); });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flushQueue();
  });
  setInterval(() => { flushQueue(); }, 20_000);

  // Listen for the Service Worker's YUMIX_FLUSH_QUEUE broadcast (background sync).
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'YUMIX_FLUSH_QUEUE') {
        flushQueue();
      }
    });
  }

  // Ask the SW to trigger background sync when we go online again.
  window.addEventListener('online', () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.ready.then((reg) => {
          if (reg && 'sync' in reg) {
            reg.sync.register('yumix-flush-queue').catch(() => {});
          }
        });
      } catch { /* */ }
    }
  });
}

/**
 * safePost — try immediate api.post; if network fails, queue for later.
 * Optimistic UIs should call this AFTER updating local state so a failure
 * doesn't block the user.
 */
export const safePost = async (url, body) => {
  if (!apiRef) throw new Error('api-not-bound');
  if (!navigator.onLine) {
    enqueueMutation({ method: 'post', url, body });
    return { queued: true };
  }
  try {
    const res = await apiRef.post(url, body);
    return res.data;
  } catch (err) {
    // Network / 5xx → queue. Client errors (4xx) shouldn't be retried.
    const status = err?.response?.status;
    if (!status || status >= 500) {
      enqueueMutation({ method: 'post', url, body });
      return { queued: true, error: err?.message };
    }
    throw err;
  }
};

// Discipline snapshot cache — used by PillarDots to render instantly
// when the app opens offline. Kept per-user to prevent cross-account leaks.
const SNAPSHOT_KEY = (userId) => `yumix_discipline_snapshot_${userId}_v1`;

export const cacheDisciplineSnapshot = (userId, snapshot) => {
  if (!userId || !snapshot) return;
  try {
    localStorage.setItem(SNAPSHOT_KEY(userId), JSON.stringify({
      snapshot,
      cached_at: new Date().toISOString(),
    }));
  } catch { /* */ }
};
export const readDisciplineSnapshot = (userId) => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.snapshot || null;
  } catch { return null; }
};
